import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import mainLogo from "assets/images/main-logo.png";
import feather from "feather-icons";
import { showToast } from "toast/toastHelper";
import { useDispatch } from "react-redux";
import { otpReq, LoginWithPassword, otpForgetPassword } from "./services/clientServices";
import { getBotInfo } from "units/bot/services/botService";
import config from "../../config.js";
import { ClipLoader, PulseLoader } from "react-spinners";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { toast } from "react-toastify";
import { Eye, EyeOff, Settings } from "lucide-react";
import { login } from "units/auth/store/authSlice";
import AnimatedBackground from "./component/AnimatedBackground";
import { useTheme } from "units/dashboard/context/ThemeProvider";
import SettingsModal from "./ClientChatBot/components/SettingsModal";
import { useTranslation } from "react-i18next";

const ClientRegister = () => {
  const { t } = useTranslation(["client"]);

  const [activeTab, setActiveTab] = useState("otp");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("null");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+98");
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const { username } = useParams();
  const [otp, setOtp] = useState();
  const [forgetOrRegister, setForgetOrRegister] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const BASE_URL = config.cdnUrl;
  const { theme } = useTheme();
  useEffect(() => {
    feather.replace();
  }, []);

  useEffect(() => {
    const fetchBotInfo = async () => {
      if (!username) return;
      try {
        const res = await getBotInfo(username);
        if (res?.ok) {
          setLogo(res.data.logo);
          setName(res.data.name);
          localStorage.setItem("logo", JSON.stringify(res.data.logo));
          setStatus(res.data.status || "unknown");
        }
      } catch (error) {
        console.error("Error fetching bot info:", error);
      }
    };
    fetchBotInfo();
  }, [username]);

  const handlePhoneChange = (value, country) => {
    setPhone(value);
    setCountryCode("+" + country.dialCode);
    setPhoneError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setPhoneError("");
    setSuccessMessage("");

    try {
      const parsed = parsePhoneNumberFromString(
        phone.startsWith("+") ? phone : "+" + phone
      );
      if (!parsed || !parsed.isValid()) {
        setPhoneError(t("register.invalidPhone"));
        setLoading(false);
        return;
      }

      const fullPhone = parsed.number;
      const pureNumber = parsed.nationalNumber;
      const countryCode = parsed.countryCallingCode;

      if (activeTab == "password") {
        const payload = {
          countryCode: "+" + countryCode,
          phone: pureNumber,
          password,
        };
        const res = await LoginWithPassword(payload);

        if (res.ok) {
          localStorage.setItem("countryCode", JSON.stringify(payload.countryCode));
          localStorage.setItem("phoneNumber", JSON.stringify(payload.phone));
          dispatch(
            login({
              accessToken: res?.data?.accessToken,
              refreshToken: res?.data?.refreshToken,
            })
          );
          navigate(`/bot/chatbox/${username}`);
        } else {
          toast.error(res?.error || t("register.invalidCredentials"));
        }

        setLoading(false);
        return;
      } else {
        const payload = {
          countryCode: "+" + countryCode,
          phone: pureNumber,
        };
        const res = await otpReq(payload);
        if (res.ok) {
          setOtp(res?.data);
          localStorage.setItem("otpCode", JSON.stringify(res?.data));
          localStorage.setItem("countryCode", JSON.stringify(payload.countryCode));
          localStorage.setItem("phoneNumber", JSON.stringify(payload.phone));
          const otpExpireTime = Date.now() + 90 * 1000;
          localStorage.setItem("otpExpireTime", otpExpireTime);
          setSuccessMessage(t("register.codeSent"));
          showToast(t("register.otpSentToast"), "success");
          let mode = "";
          if (forgetOrRegister) {
            mode = "ForgetOrRegister";
          } else {
            mode = "LoginWithOtp";
          }
          navigate(`/otp-verification/${username}`, {
            state: { mode: mode },
          });
        }

        if (!res.ok) {
          setOtpTimer(res?.error?.remainingSeconds);
          toast.error(res?.error?.message);
        }
      }
    } catch (error) {
      console.log(error, "error");
      setError(error.message || t("register.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otpTimer <= 0) return;

    const timer = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpTimer]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(
      /[^A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g,
      ""
    );
    setPassword(filteredValue);
  };

  const forgetOrRegisterHandler = () => {
    setForgetOrRegister(!forgetOrRegister);
    setActiveTab("otp");
  };

  return (
    <div className="relative min-h-screen flex justify-center items-center overflow-hidden">
      <AnimatedBackground />

      <style>{`
        @property --ring-angle {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes ringTravel {
          to { --ring-angle: 360deg; }
        }

        .ring-traveler {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 2px;
          background: conic-gradient(
            from var(--ring-angle),
            transparent 0deg,
            transparent 250deg,
            rgba(20, 184, 166, 0.15) 270deg,
            #14b8a6 300deg,
            #FFC505 330deg,
            #fff7c2 345deg,
            transparent 360deg
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          animation: ringTravel 4s linear infinite;
          pointer-events: none;
          z-index: 20;
        }

        .ring-glow {
          position: absolute;
          inset: -2px;
          border-radius: inherit;
          padding: 3px;
          background: conic-gradient(
            from var(--ring-angle),
            transparent 0deg,
            transparent 260deg,
            #14b8a6 305deg,
            #FFC505 335deg,
            transparent 360deg
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          filter: blur(8px);
          opacity: 0.85;
          animation: ringTravel 4s linear infinite;
          pointer-events: none;
          z-index: 19;
        }

        .glass-card {
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.78) 0%,
            rgba(255, 255, 255, 0.55) 45%,
            rgba(20, 184, 166, 0.10) 100%
          );
          backdrop-filter: blur(24px) saturate(160%);
          -webkit-backdrop-filter: blur(24px) saturate(160%);
          border: 1px solid rgba(255, 255, 255, 0.55);
        }
        .glass-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background:
            radial-gradient(circle at 20% 0%, rgba(255, 197, 5, 0.15), transparent 55%),
            radial-gradient(circle at 100% 100%, rgba(20, 184, 166, 0.15), transparent 55%);
        }
        .dark .glass-card {
          background: linear-gradient(
            135deg,
            rgba(15, 63, 58, 0.55) 0%,
            rgba(10, 35, 32, 0.65) 45%,
            rgba(20, 184, 166, 0.10) 100%
          );
          border: 1px solid rgba(94, 234, 212, 0.25);
        }

        .ltr-phone-input { direction: ltr; text-align: left; }
        .ltr-phone-input .form-control {
          direction: ltr !important;
          text-align: left !important;
          padding-left: 2.5rem !important;
          caret-color: black;
        }
        .ltr-phone-input input {
          direction: ltr;
          text-align: left;
          padding-right: 3.5rem;
          padding-left: 3.5rem;
        }
        .react-tel-input .country-list .country-name { margin-left: 30px; }
        .ltr-phone-input .flag-dropdown { pointer-events: auto; }
        .react-tel-input .selected-flag:focus,
        .react-tel-input .selected-flag:hover,
        .react-tel-input .selected-flag.open {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
        }
        .react-tel-input .selected-flag:focus:before,
        .react-tel-input .selected-flag.open:before {
          border-color: white !important;
          box-shadow: none !important;
        }
        .react-tel-input .invalid-number-message,
        .react-tel-input .error-msg { display: none !important; }
        .react-tel-input .form-control { direction: ltr; text-align: left; }

        /* دارک مود برای PhoneInput */
        .dark .ltr-phone-input .form-control {
          background: rgba(13, 60, 55, 0.5) !important;
          color: #ccfbf1 !important;
          border-color: rgba(94, 234, 212, 0.3) !important;
        }
        .dark .ltr-phone-input input::placeholder {
          color: rgba(153, 246, 228, 0.5) !important;
        }
        .dark .react-tel-input .flag-dropdown {
          background: rgba(13, 60, 55, 0.5) !important;
          border-color: rgba(94, 234, 212, 0.3) !important;
        }
        .dark .react-tel-input .selected-flag:hover,
        .dark .react-tel-input .selected-flag:focus {
          background: rgba(20, 184, 166, 0.15) !important;
        }
        .dark .react-tel-input .country-list {
          background: #0f2e2a !important;
          color: #ccfbf1 !important;
          border: 1px solid rgba(94, 234, 212, 0.25) !important;
        }
        .dark .react-tel-input .country-list .country:hover {
          background: rgba(20, 184, 166, 0.2) !important;
        }
        .dark .react-tel-input .country-list .country.highlight {
          background: rgba(20, 184, 166, 0.3) !important;
        }
        .dark .react-tel-input .country-list .search {
          background: #0f2e2a !important;
        }
        .dark .react-tel-input .country-list .search-box {
          background: rgba(13, 60, 55, 0.7) !important;
          color: #ccfbf1 !important;
          border-color: rgba(94, 234, 212, 0.3) !important;
        }
      `}</style>

      <div className="relative z-10 max-w-md w-[350px] mx-auto">
        <div className="relative rounded-3xl glass-card shadow-[0_20px_60px_-15px_rgba(20,184,166,0.35),0_8px_30px_-10px_rgba(255,197,5,0.25)] dark:!shadow-[0_20px_60px_-15px_rgba(20,184,166,0.5),0_8px_30px_-10px_rgba(255,197,5,0.35)]">
          <div className="ring-glow rounded-3xl" />
          <div className="ring-traveler rounded-3xl" />

          {/* 👇 دکمه‌ی تنظیمات — گوشه‌ی کارت */}
          <button
            type="button"
            onClick={() => setIsSettingsOpen(true)}
            aria-label={t("register.settings")}
            className="
      absolute top-3 end-3 z-30
      w-9 h-9 flex items-center justify-center rounded-xl
      text-gray-500 dark:!text-teal-200/70
      bg-white/50 dark:!bg-teal-900/40
      backdrop-blur-md
      border border-white/60 dark:!border-teal-300/20
      hover:text-teal-700 dark:!hover:text-yellow-200
      hover:bg-white/80 dark:!hover:bg-teal-800/60
      transition-all duration-200
      active:scale-95
    "
          >
            <Settings className="w-4 h-4" />
          </button>

          <div className="relative z-10 p-8 rounded-3xl overflow-hidden">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/60 dark:!from-teal-200/10 to-transparent rounded-t-3xl" />

            {otpTimer > 0 && (
              <div className="absolute w-12 h-12 left-2 top-2 z-20">
                <ClipLoader color="#FFC505" size={48} speedMultiplier={0.5} loading={true} />
                <span className="absolute inset-0 flex items-center justify-center text-gray-700 dark:!text-teal-100 text-sm font-bold">
                  {otpTimer}
                </span>
              </div>
            )}

            <div className="relative z-10 border-3 border-gray-400 rounded-full w-fit mx-auto">
              <Link to={`/${username}`}>
                <img
                  src={logo ? `${BASE_URL}${logo}` : "/defaultLogo.png"}
                  alt="logo"
                  className="w-[150px] h-[150px] mx-auto block rounded-full object-cover mb-5 border-3 ring-2 ring-teal-200/70 dark:!ring-teal-400/40 shadow-[0_0_25px_-5px_rgba(255,197,5,0.55)] dark:!shadow-[0_0_25px_-5px_rgba(20,184,166,0.65)]"
                  onError={(e) => {
                    e.currentTarget.src = "/defaultLogo.png";
                  }}
                />
              </Link>
            </div>

            <h2 className="relative z-10 text-[#0f3f3a] dark:!text-teal-50 text-center text-lg flex flex-col justify-center items-center gap-y-2">
              <span className="font-bold bg-gradient-to-r from-teal-700 via-[#1f3e39] to-yellow-600 dark:!from-teal-300 dark:!via-teal-100 dark:!to-yellow-300 bg-clip-text text-transparent">
                {`${name?.trim() || username}`}
              </span>
              <span className="font-medium text-gray-600 dark:!text-teal-100/80">
                {t("register.welcome")}
              </span>
            </h2>

            {forgetOrRegister ? null : (
              <div className="relative z-10 flex justify-center mb-6 mt-4 border-b border-white/60 dark:!border-teal-300/20">
                <button
                  type="button"
                  onClick={() => setActiveTab("password")}
                  className={`w-1/2 py-3 text-center font-medium transition-all ${activeTab === "password"
                    ? "border-b-2 border-yellow-400 text-yellow-700 dark:!border-yellow-300 dark:!text-yellow-300"
                    : "text-gray-500 hover:text-teal-700 dark:!text-teal-200/70 dark:!hover:text-yellow-200"
                    }`}
                >
                  {t("register.loginWithPassword")}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("otp")}
                  className={`w-1/2 py-3 text-center font-medium transition-all ${activeTab === "otp"
                    ? "border-b-2 border-yellow-400 text-yellow-700 dark:!border-yellow-300 dark:!text-yellow-300"
                    : "text-gray-500 hover:text-teal-700 dark:!text-teal-200/70 dark:!hover:text-yellow-200"
                    }`}
                >
                  {t("register.loginWithOtp")}
                </button>
              </div>
            )}

            <form className="relative z-10 mt-6" onSubmit={handleSubmit}>
              <div>
                <PhoneInput
                  country={"ir"}
                  value={phone}
                  onChange={handlePhoneChange}
                  disableCountryCode={false}
                  countryCodeEditable={false}
                  excludeCountries={["tr", "af", "us"]}
                  inputClass="!w-full !text-gray-800 dark:!!text-teal-50 !text-sm !px-4 !rounded-xl !border !border-white/70 dark:!!border-teal-300/30 !bg-white/60 dark:!!bg-teal-900/40 !backdrop-blur-md !py-5 focus:!ring-2 focus:!ring-yellow-300 focus:!border-teal-300"
                  searchClass="!flex !justify-center !text-center !w-[98%] !mr-20"
                  buttonClass="!border-none !bg-transparent !mr-2 !p-0"
                  containerClass="ltr-phone-input"
                  enableSearch={true}
                  disableSearchIcon={true}
                  placeholder={t("register.phonePlaceholder")}
                  inputProps={{
                    style: { direction: "ltr", textAlign: "left" },
                    onKeyDown: (e) => {
                      const cursorPos = e.target.selectionStart;
                      if (
                        (e.key === "Backspace" || e.key === "Delete") &&
                        cursorPos <= countryCode.length
                      ) {
                        e.preventDefault();
                      }
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const form = e.target.closest("form");
                        if (form) form.requestSubmit();
                      }
                    },
                  }}
                />

                {activeTab == "otp" ? null : (
                  <div className="relative mt-2">
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder={t("register.passwordPlaceholder")}
                        className="w-full border border-white/70 dark:!border-teal-300/30 bg-white/60 dark:!bg-teal-900/40 backdrop-blur-md rounded-xl p-3 pe-10 text-gray-800 dark:!text-teal-50 placeholder:text-gray-500 dark:!placeholder:text-teal-200/50 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-teal-300 placeholder:text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 end-3 flex items-center text-gray-500 dark:!text-teal-200/70 hover:text-teal-700 dark:!hover:text-yellow-200"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {phoneError && (
                <p className="text-red-600 dark:!text-red-400 text-sm mt-1">
                  {phoneError}
                </p>
              )}
              {successMessage && (
                <p className="text-green-600 dark:!text-emerald-400 text-sm mt-1">
                  {successMessage}
                </p>
              )}

              <div className="mt-4">
                <button
                  disabled={loading || otpTimer > 0}
                  type="submit"
                  className="
                    relative w-full py-3 px-4 text-sm tracking-wide rounded-xl
                    text-white font-bold

                    bg-gradient-to-r from-teal-700 via-teal-600 to-teal-500
                    dark:!from-teal-600 dark:!via-teal-500 dark:!to-teal-400

                    hover:from-teal-800 hover:via-teal-700 hover:to-teal-600
                    dark:!hover:from-teal-500 dark:!hover:via-teal-400 dark:!hover:to-teal-300

                    shadow-[0_10px_25px_-8px_rgba(13,148,136,0.7)]
                    dark:!shadow-[0_10px_25px_-8px_rgba(20,184,166,0.9)]

                    hover:shadow-[0_12px_30px_-6px_rgba(20,184,166,0.8)]
                    dark:!hover:shadow-[0_12px_30px_-6px_rgba(94,234,212,0.9)]

                    transition-all duration-300

                    focus:outline-none
                    focus:ring-2 focus:ring-offset-2 focus:ring-teal-500
                    dark:!focus:ring-teal-300

                    disabled:opacity-70
                    disabled:cursor-not-allowed
                  "
                >
                  {!loading ? (
                    activeTab == "otp" ? (
                      t("register.sendCode")
                    ) : (
                      t("register.login")
                    )
                  ) : (
                    <PulseLoader color="#ffffff" size={9} />
                  )}
                </button>
              </div>

              <div className="flex justify-between items-center mt-2">
                <button
                  type="button"
                  onClick={forgetOrRegisterHandler}
                  className="mt-2 w-full text-center text-sm text-teal-700 dark:!text-teal-300 font-bold hover:text-yellow-700 dark:!hover:text-yellow-300 hover:underline transition-colors"
                >
                  {forgetOrRegister
                    ? t("register.backToTabs")
                    : t("register.createOrForgotPassword")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
};

export default ClientRegister;